import { Box, Button, SidePanel, Tag } from '@dev-spendesk/grapes';
import React from 'react';
import { useHistory, useLocation, matchPath } from 'react-router-dom';

import { SupplierLogo } from 'common/components/SupplierLogo';
import { useFeature } from 'common/hooks/useFeature';
import { useParams } from 'common/hooks/useParams';
import { useTranslation } from 'common/hooks/useTranslation';
import { type IntegrationStatusWithIntegration } from 'modules/bookkeep/integration/status';
import { useSupplierSyncStatus } from 'modules/suppliers/hooks';
import { EllipsisTooltip } from 'src/core/common/components/EllipsisTooltip';
import FEATURES from 'src/core/constants/features';
import { routes, routeFor } from 'src/core/constants/routes';

import { AccountPayableSuppliersCreditNotesSection } from './AccountPayableSuppliersCreditNotesSection';
import { AccountPayableSuppliersPanelAccountingSection } from './AccountPayableSuppliersPanelAccountingSection';
import { AccountPayableSuppliersPanelBankDetailsSection } from './AccountPayableSuppliersPanelBankDetailsSection';
import { AccountPayableSuppliersPanelBankProofSection } from './AccountPayableSuppliersPanelBankProofSection';
import { AccountPayableSuppliersPanelLegalDetailsSection } from './AccountPayableSuppliersPanelLegalDetailsSection';
import { AccountPayableSuppliersPanelVendorSelect } from './AccountPayableSuppliersPanelVendorSelect';
import { AccountPayableSuppliersSyncStatus } from './AccountPayableSuppliersSyncStatus';
import { type SupplierDetails } from '../../types';

type Props = {
  integrationStatus: IntegrationStatusWithIntegration;
  supplier: SupplierDetails;
  selectedTab: 'current' | 'archived';
  mustDisplayAccountsPayable: boolean;
  onArchiveSupplier(): void;
  onUnarchiveSupplier(): void;
};

export const SuppliersPanelId = 'suppliers-panel';

export const AccountPayableSuppliersPanel = ({
  integrationStatus,
  supplier,
  selectedTab,
  mustDisplayAccountsPayable,
  onArchiveSupplier,
  onUnarchiveSupplier,
}: Props) => {
  const { t } = useTranslation('global');
  const history = useHistory();
  const location = useLocation();
  const { company } = useParams(
    routes.COMPANY_ACCOUNTS_PAYABLE_SUPPLIER_DETAIL.path,
  );
  const hasAutocatFeature = useFeature(FEATURES.AUTO_CAT);
  const supplierSyncStatus = useSupplierSyncStatus(supplier.id);

  const matchSupplierDetails = matchPath<{ supplierId: string }>(
    location.pathname,
    {
      path: routes.COMPANY_ACCOUNTS_PAYABLE_SUPPLIER_DETAIL.path,
      exact: true,
      strict: false,
    },
  );
  const matchAccountPayableSuppliersDetail = matchPath<{
    accountPayableId: string;
  }>(location.pathname, {
    path: routes.COMPANY_ACCOUNTS_PAYABLE_SUPPLIERS_DETAIL.path,
    exact: true,
    strict: false,
  });

  const handleSuppliersPanelClose = () => {
    if (matchSupplierDetails) {
      history.push({
        pathname: routeFor(routes.COMPANY_ACCOUNTS_PAYABLE.path, {
          company,
          list: selectedTab,
        }),
        search: location.search,
      });
    }
    if (matchAccountPayableSuppliersDetail) {
      history.push({
        pathname: routeFor(routes.COMPANY_ACCOUNTS_PAYABLE_SUPPLIERS.path, {
          accountPayableId:
            matchAccountPayableSuppliersDetail?.params.accountPayableId ?? '',
          company,
          list: selectedTab,
        }),
        search: location.search,
      });
    }
  };
  const integrationName = integrationStatus.integration;

  return (
    <SidePanel
      title={t('bookkeep.accountsPayable.panel.headerTitle')}
      data-testid={SuppliersPanelId}
      onClose={() => {
        handleSuppliersPanelClose();
      }}
      header={
        <div className="-mb-24 flex w-full flex-col gap-8">
          <Box
            variant="secondary"
            className="relative flex min-h-[80px] flex-col justify-center gap-8 py-16"
          >
            <div className="flex items-center">
              <SupplierLogo
                name={supplier?.id === 'unknown' ? '' : supplier?.name}
                size={32}
                lazyLoad
              />

              <h3 className="text-content-primary title-xl ml-12">
                {supplier.name}
              </h3>

              <Tag variant="white" className="ml-auto min-w-32">
                <EllipsisTooltip
                  className="max-w-[150px]"
                  text={
                    supplier.supplierAccount?.generalAccountCode ||
                    t('bookkeep.accountsPayable.undefined')
                  }
                />
              </Tag>
            </div>

            {supplierSyncStatus.status === 'success' && (
              <AccountPayableSuppliersSyncStatus
                syncStatus={supplierSyncStatus.data}
                integrationName={integrationName}
              />
            )}
          </Box>

          <AccountPayableSuppliersCreditNotesSection
            creditNotes={supplier.creditNotes}
          />
        </div>
      }
      footer={
        <Button
          fit="parent"
          text={
            supplier.isArchived === false
              ? t('bookkeep.accountsPayable.panel.archive')
              : t('bookkeep.accountsPayable.panel.unarchive')
          }
          variant={supplier.isArchived ? 'secondaryBrand' : 'secondaryAlert'}
          iconName={supplier.isArchived === false ? 'archive' : undefined}
          onClick={() => {
            if (supplier.isArchived === false) {
              onArchiveSupplier();
            } else {
              onUnarchiveSupplier();
            }

            history.push(
              routeFor(routes.COMPANY_ACCOUNTS_PAYABLE.path, {
                company,
                list: selectedTab,
              }),
            );
          }}
        />
      }
    >
      {supplierSyncStatus.status === 'success' && (
        <AccountPayableSuppliersPanelVendorSelect
          supplier={supplier}
          integrationName={integrationName}
        />
      )}

      {(mustDisplayAccountsPayable || hasAutocatFeature) && (
        <AccountPayableSuppliersPanelAccountingSection
          integrationStatus={integrationStatus}
          supplier={supplier}
          selectedTab={selectedTab}
          mustDisplayAccountsPayable={mustDisplayAccountsPayable}
        />
      )}

      {supplier.bankDetails !== null && (
        <div>
          <AccountPayableSuppliersPanelBankDetailsSection supplier={supplier} />
          <div className="mt-24">
            <AccountPayableSuppliersPanelBankProofSection supplier={supplier} />
          </div>
        </div>
      )}

      {supplier.legalDetails !== null && (
        <AccountPayableSuppliersPanelLegalDetailsSection supplier={supplier} />
      )}
    </SidePanel>
  );
};
